import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import { auditByStatusByDate } from "../../graphql/queries";
import { countInboundShipments } from "../../graphql/inbound/custom-queries";
import { countOutboundShipments } from "../../graphql/outbound/custom-queries";

import {
  Button,
  CardActions,
  Grid,
  Typography,
  CardContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useNotify, Title } from "react-admin";

import { Tasks } from "../../crewApp/pages/myTasks/MyTasks";

import {
  AuditOutlinedIcon,
  InbounddOutlinedIcon,
  OutbounddOutlinedIcon,
} from "../../components/common/iconography";

import {
  AuditStatus,
  InboundState,
  InboundStatus,
  OutboundStatus,
  PortalType,
} from "../../utils/constants";

import { logError } from "../../utils/appUtils";
import CrewCard from "../../components/common/CrewCard";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    // marginTop: theme.spacing(1),
    // marginLeft: theme.spacing(1),
    // marginRight: theme.spacing(1),
    // marginBottom: theme.spacing(1),
  },
  card: {
    minHeight: 150,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flex: "1",
    "& a": {
      textDecoration: "none",
      color: "inherit",
    },
  },
  cardAvatar: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  cardItemLeft: {
    display: "flex",
    justifyContent: "flex-end",
  },
  cardItemRight: {
    display: "flex",
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  cardActions: {
    marginTop: "auto",
    "& a": {
      textDecoration: "none",
      color: "inherit",
    },
  },
}));

export const ManagerDashboard = (props) => {
  const classes = useStyles();
  const portalType = localStorage.getItem("portalType") || PortalType.MANAGER;

  return (
    <div className={classes.root}>
      <Title title=" " />
      <Grid container>
        <Grid item xs={12} sm={6}>
          <InboundCard classes={classes} portalType={portalType} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <OutboundCard classes={classes} portalType={portalType} />
        </Grid>
        {/* <Grid item xs={12} sm={4} >
					<AuditCard classes={classes} portalType={portalType} />
				</Grid> */}
        <Grid item xs={12}>
          <Tasks {...props} hideTitle={true} redirectTo="/" />
        </Grid>
      </Grid>
    </div>
  );
};

const InboundCard = ({ classes, portalType }) => {
  const [loaded, setLoaded] = useState();
  const [summary, setSummary] = useState({
    rolling: 0,
    notified: 0,
    placed: 0,
    completed: 0,
  });
  const notify = useNotify();

  useEffect(() => {
    !loaded && init();
  }, []);

  const init = async () => {
    setLoaded(true);
    try {
      const queryList = [];
      queryList[0] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: window.appUser?.reloadYardId },
            or: [
              { status: { eq: InboundStatus.IN_TRANSIT } },
              { status: { eq: InboundStatus.SIT_PENDING } },
            ],
          },
        })
      );

      queryList[1] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: window.appUser?.reloadYardId },
            status: { eq: InboundStatus.ARRIVED },
          },
        })
      );

      queryList[2] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: window.appUser?.reloadYardId },
            // SIT cars stay PROCESS_SHIPMENT while placed (COMPLETED only once
            // released), so they only ever land in Placed, never Completed.
            or: [
              { status: { eq: InboundStatus.PLACED } },
              { status: { eq: InboundStatus.SIT_PLACED } },
            ],
            state: { ne: InboundState.COMPLETED },
          },
        })
      );

      queryList[3] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: window.appUser?.reloadYardId },
            status: { eq: InboundStatus.PLACED },
            state: { eq: InboundState.COMPLETED },
          },
        })
      );

      const promiseResults = await Promise.all(queryList);

      let rollingCount = promiseResults[0].data.searchInboundShipments.total;
      let notifiedCount = promiseResults[1].data.searchInboundShipments.total;
      let placedCount = promiseResults[2].data.searchInboundShipments.total;
      let completedCount = promiseResults[3].data.searchInboundShipments.total;

      setSummary({
        rolling: rollingCount ? rollingCount : 0,
        notified: notifiedCount ? notifiedCount : 0,
        placed: placedCount ? placedCount : 0,
        completed: completedCount ? completedCount : 0,
      });
    } catch (err) {
      logError("components:Dashboard:InboundCard:init", err, notify);
    }
  };

  return (
    <CrewCard
      showDivider={false}
      title="Inbound"
      icon={<InbounddOutlinedIcon fontSize="large" />}
      addMargin={true}
    >
      <CardContent>
        <div className={classes.card}>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.rolling}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                In Transit
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.notified}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Notified
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.placed}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Placed
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.completed}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Completed
              </Typography>
            </Grid>
          </Grid>
        </div>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Link
          to={
            portalType === PortalType.CREW
              ? "/processShipments"
              : "/inboundShipments"
          }
        >
          <Button size="large">View</Button>
        </Link>
      </CardActions>
    </CrewCard>
  );
};

const OutboundCard = ({ classes, portalType }) => {
  const [loaded, setLoaded] = useState();
  const [summary, setSummary] = useState({
    pending: 0,
    arrived: 0,
    placed: 0,
    loaded: 0,
  });
  const notify = useNotify();

  useEffect(() => {
    !loaded && init();
  }, []);

  const init = async () => {
    if (!loaded) {
      setLoaded(true);
      try {
        const queryList = [];
        queryList[0] = API.graphql(
          graphqlOperation(countOutboundShipments, {
            filter: {
              reloadYardId: { eq: window.appUser?.reloadYardId },
              status: { eq: OutboundStatus.PENDING },
            },
          })
        );

        queryList[1] = API.graphql(
          graphqlOperation(countOutboundShipments, {
            filter: {
              reloadYardId: { eq: window.appUser?.reloadYardId },
              status: { eq: OutboundStatus.ARRIVED },
            },
          })
        );

        queryList[2] = API.graphql(
          graphqlOperation(countOutboundShipments, {
            filter: {
              reloadYardId: { eq: window.appUser?.reloadYardId },
              status: { eq: OutboundStatus.PLACED },
            },
          })
        );

        queryList[3] = API.graphql(
          graphqlOperation(countOutboundShipments, {
            filter: {
              reloadYardId: { eq: window.appUser?.reloadYardId },
              status: { eq: OutboundStatus.LOADED },
            },
          })
        );

        const promiseResults = await Promise.all(queryList);

        let pendingCount = promiseResults[0].data.searchOutboundShipments.total;
        let arrivedCount = promiseResults[1].data.searchOutboundShipments.total;
        let placedCount = promiseResults[2].data.searchOutboundShipments.total;
        let loadedCount = promiseResults[3].data.searchOutboundShipments.total;

        setSummary({
          pending: pendingCount ? pendingCount : 0,
          arrived: arrivedCount ? arrivedCount : 0,
          placed: placedCount ? placedCount : 0,
          loaded: loadedCount ? loadedCount : 0,
        });
      } catch (err) {
        logError("components:Dashboard:OutboundCard:init", err, notify);
      }
    }
  };

  return (
    <CrewCard
      showDivider={false}
      title="Outbound"
      icon={<OutbounddOutlinedIcon fontSize="large" />}
      addMargin={true}
    >
      <CardContent>
        <div className={classes.card}>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.pending}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Pending
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.arrived}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Arrived
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.placed}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Placed
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.loaded}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Loaded
              </Typography>
            </Grid>
          </Grid>
        </div>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Link
          to={
            portalType === PortalType.CREW
              ? "/processOutboundShipments"
              : "/outboundShipments"
          }
        >
          <Button size="large">View</Button>
        </Link>
      </CardActions>
    </CrewCard>
  );
};

const AuditCard = ({ classes, portalType }) => {
  const [, setMessage] = useState();
  const [loaded, setLoaded] = useState();
  const [summary, setSummary] = useState({ total: 0, noMatchCount: 0 });
  const [audit, setAudit] = useState();
  const notify = useNotify();

  useEffect(() => {
    !loaded && init();
  }, []);

  const init = async () => {
    setLoaded(true);
    try {
      const reloadYardId = window.appUser?.reloadYardId;
      const response = await API.graphql(
        graphqlOperation(auditByStatusByDate, {
          reloadYardId,
          statusCreateDateTime: {
            beginsWith: { status: AuditStatus.COMPLETED },
          },
          limit: 1,
          sortDirection: "DESC",
        })
      );
      let audits = response.data.auditByStatusByDate.items;

      if (audits.length > 0) {
        const lastAudit = audits[0];
        setAudit(lastAudit);
        lastAudit.processedItems = !lastAudit.processedItems
          ? []
          : JSON.parse(lastAudit.processedItems);
        setMessage(
          `Last audit Date ${formatDistanceToNow(
            new Date(lastAudit.createdAt)
          )} ago`
        );
        let matchCount = 0;
        let noMatchCount = 0;
        let itemsNotFound = 0;
        let total = lastAudit.processedItems.length;
        lastAudit.processedItems.forEach((itm) => {
          if (itm.quantityPhysical === 0) itemsNotFound++;
          else if (itm.quantityPhysical !== itm.quantityOnHand) noMatchCount++;
          else if (itm.quantityPhysical === itm.quantityOnHand) matchCount++;
        });
        setSummary({
          matchCount,
          noMatchCount,
          itemsNotFound,
          total,
        });
      }
    } catch (err) {
      logError("components:Dashboard:AuditCard:init", err, notify);
    }
  };

  return (
    <CrewCard
      showDivider={false}
      title="Audit"
      icon={<AuditOutlinedIcon fontSize="large" />}
      addMargin={false}
    >
      <CardContent>
        <div className={classes.card}>
          {summary.matchCount ? (
            <Grid container spacing={1}>
              <Grid item xs={2}>
                <Typography className={classes.cardItemLeft} variant="h4">
                  {summary.total}
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography
                  className={classes.cardItemRight}
                  variant="h6"
                  color="textSecondary"
                >
                  Total
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.cardItemLeft} variant="h4">
                  {summary.noMatchCount}
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography
                  className={classes.cardItemRight}
                  variant="h6"
                  color="textSecondary"
                >
                  Discrepancies
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography>No Audit found</Typography>
          )}
        </div>
      </CardContent>
      <CardActions className={classes.cardActions}>
        {audit ? (
          <Link
            to={portalType === PortalType.CREW ? "/processAudit" : "/audits"}
          >
            <Button size="large">View</Button>
          </Link>
        ) : (
          <Button size="large" disabled>
            View
          </Button>
        )}
      </CardActions>
    </CrewCard>
  );
};
